import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { of, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";
import { conformToMask } from "text-mask-core";

import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { StructuredOperationAssetsSubtypesEnum } from "../../enum/structured-operation-assets-subtypes.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import { Bank } from "../../model/domain/Bank";
import { CalendarModel } from "../../model/domain/CalendarModel";
import {
  CounterpartType,
  InterestType,
  OperationType,
  SettlementType,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { StructuredOperationOrder } from "../../model/domain/StructuredOperationOrder";
import { StructuredOperationTypeFileModel } from "../../model/domain/StructuredOperationTypeFileModel";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CalendarService } from "../../services/calendar.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { DomainService } from "../../services/domain.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { CounterpartComponent } from "../counterpart/counterpart.component";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-ordem-operacao-estruturada",
  templateUrl: "./ordem-operacao-estruturada.component.html",
  styleUrls: ["./ordem-operacao-estruturada.component.css"],
})
export class OrdemOperacaoEstruturadaComponent
  implements OnInit, OnDestroy, OnChanges
{
  public model: StructuredOperationOrder = null;

  faInfoCircle = faInfoCircle;

  navigationSubscription;

  MenuConstants = MenuConstants;

  isEditing = false;
  canValidate = false;
  isSubmit = false;
  isComponent = false;
  uploadedFilesCount = 0;

  decimalPipe = new DecimalPipe("pt");

  @Input() mainTicketId: number = null;
  @Input() orderId: number = null;
  @Input() totalApprovedAssetsValue: number = null;
  @Input() totalApprovedAssetsAmount: number = null;
  @Input() totalOrdersValue: number = null;
  @Input() totalOrdersAmount: number = null;
  @Input() biggestLimitTimestamp: number = null;
  @Input() structuredOperationAssetId: number = null;
  @Input() structuredOperationAssetSubtypeId: number = null;
  @Output() closePage: EventEmitter<any> = new EventEmitter();
  @Output() openPage: EventEmitter<any> = new EventEmitter();
  @Output() reloadTickets: EventEmitter<any> = new EventEmitter();
  @Output() dataChanged: EventEmitter<string> = new EventEmitter();

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundoComponent: DadosFundoComponent;
  @ViewChild("counterpart", { static: true })
  counterpartComponent: CounterpartComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  TicketTypeEnum = TicketType;

  TiposCompra: NamedType<number>[];
  ListaTipoJuros: NamedType<number>[];
  ListaBaseIndexador: NamedType<number>[];
  ListaTipoLiquidacao: NamedType<number>[];
  ListaTipoDocumento: NamedType<number>[];
  ListaNoFinancial: NamedType<number>[];
  ListaTipoDoDoAtivo: NamedType<number>[];
  ListaTipoDaContraparte: NamedType<number>[];
  banksList: Bank[] = null;
  hasPropositionIsDisabled = false;
  holidayList: CalendarModel[];

  isOtherAsset: boolean;
  isRealty: boolean;
  operationTypeFile: StructuredOperationTypeFileModel = null;
  isNotProduction = environment.name != 'production';

  clusterAFieldNames: String[] = [
    "operationTypeId",
    "amount",
    "unitPrice",
    "operationValue",
    "assetType",
    "assetCode",
    "expirationDate",
    "issueFee",
    "issueDate",
    "issuer",
    "index",
    "indexPercent",
    "indexTax",
    "indexBase",
    "interestType",
    "settlementDate"
  ];

  clusterBFieldNames: String[] = [
    "operationTypeId",
    "operationValue",
    "assetType",
  ];

  clusterCFieldNames: String[] = [
    "operationTypeId",
    "operationValue",
    "assetType",
    "officialRequisition"
  ];

  clusterDFieldNames: String[] = [
    "operationTypeId",
    "amount",
    "unitPrice",
    "operationValue",
    "assetType"
  ];

  clusterEFieldNames: String[] = [
    "operationTypeId",
    "operationValue",
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TicketService,
    public validator: ValidatorService,
    private datePipe: DatePipe,
    private authService: AuthService,
    private dialogService: DialogService,
    private snackBarService: SnackbarService,
    private location: Location,
    private loaderService: LoaderService,
    private domainService: DomainService,
    private cutoffService: CutoffService,
    private calendarService: CalendarService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        if (!this.isSubmit) {
          this.resetForm();
        }
      }
    });
  }

  validators = {
    settlementTypeId: (value) =>
      [
        SettlementType.TED,
        SettlementType.CETIP,
        SettlementType.NoFinancial,
      ].includes(value),
    operationTypeId: (value) =>
      [
        OperationType.Redemption,
        OperationType.Application,
        OperationType.Deposit,
        OperationType.Withdrawal,
      ].includes(value),
    isSecondaryMarket: (value) =>
      this.isTED() || this.isCETIP() ? [true, false].includes(value) : true,
    operationValue: (value) =>
      !this.isFieldAvailable('operationValue') ? true : this.model.id == 0 && (this.isCETIP() || this.isTED())
        ? this.operationValueValidator(value)
        : true,
    indexPercent: (value) =>
      !this.isFieldAvailable('indexPercent') ? true : this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.decimalIndexFeeMask, {}),
    interestType: (value) =>
      !this.isFieldAvailable('interestType') ? true : this.isSell() || this.isPurchase()
        ? [InterestType.Exponencial, InterestType.Linear].includes(value)
        : true,
    indexBase: (value) =>
      !this.isFieldAvailable('indexBase') ? true : this.isSell() || this.isPurchase() ? [1, 2].includes(value) : true,
    amount: (value) =>
      !this.isFieldAvailable('amount') ? true : this.coalesce(value) > 0 && this.model.id == 0
        ? this.amountValidator(value)
        : true,
    unitPrice: (value) => !this.isFieldAvailable('unitPrice') ? true : this.coalesce(value) >= 0,
    expirationDate: (value) =>
      !this.isFieldAvailable('expirationDate') ? true : this.validateDate(value) && DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    issueDate: (value) =>
      !this.isFieldAvailable('issueDate') ? true : value != null && DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    settlementDate: (value) => 
      !this.isFieldAvailable('settlementDate') ? true : this.validateDate(value) && this.validateMaxSettlementDate(value) && DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
  };

  ngOnChanges() {
    this.isComponent = true;
    this.resetForm();
    this.getTicket(this.orderId);
  }

  ngOnInit() {
    this.operationTypeFile = new StructuredOperationTypeFileModel();
    this.operationTypeFile.structuredOperationAssetId = this.structuredOperationAssetId;
    this.operationTypeFile.structuredOperationAssetSubtypeId = this.structuredOperationAssetSubtypeId;

    this.isSubmit = false;
    this.getBanksList();
    if (!this.isComponent) this.getTicket();
    this.getHolidayList();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.frm.resetForm();
    this.model = new StructuredOperationOrder();

    this.annotationComponent.annotation = null;
    this.annotationComponent.annotationIsDisabled = false;

    this.isEditing = false;
    this.canValidate = false;
    this.model.id = null;
    this.model.command = "0";
    this.model.isSecondaryMarket = false;
    this.model.assetType = "-1";
    this.model.settlementTypeId = SettlementType.CETIP;

    this.model.operationDate = new Date();

    this.isSubmit = false;
    this.setEnable(true);
  }

  setTempId(tempId) {
    this.model.tempTicketId = tempId;
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundoComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(idBoleta: number = null): void {
    this.model = new StructuredOperationOrder();
    this.model.isSecondaryMarket = false;
    // this.model.assetType = "-1";
    this.TiposCompra = this.service.ListStructuredOrderOperationTypes();
    this.ListaBaseIndexador = this.service.ListFoundationIndexes();
    this.ListaTipoJuros = this.service.ListInterestTypes();
    this.ListaTipoLiquidacao = this.service.ListCetipSettlementTypes();
    this.ListaTipoDocumento = this.service.ListTypeOfDocument();
    this.ListaNoFinancial = this.service.ListCetipNoFinancialOperationTypes();
    this.ListaTipoDoDoAtivo = this.service.ListAssetCode();
    this.ListaTipoDaContraparte = this.service.ListCounterparTypes();

    var id: number;

    if (this.isComponent) {
      id = idBoleta;
    } else {
      id = idBoleta ?? +this.route.snapshot.paramMap.get("id");
      this.mainTicketId =
        +this.route.snapshot.queryParamMap.get("mainTicketId");
      this.totalApprovedAssetsValue = +this.route.snapshot.queryParamMap.get(
        "totalApprovedAssetsValue"
      );
      this.totalApprovedAssetsAmount = +this.route.snapshot.queryParamMap.get(
        "totalApprovedAssetsAmount"
      );
      this.totalOrdersValue =
        +this.route.snapshot.queryParamMap.get("totalOrdersValue");
      this.totalOrdersAmount =
        +this.route.snapshot.queryParamMap.get("totalOrdersAmount");
      this.biggestLimitTimestamp = +this.route.snapshot.queryParamMap.get("biggestLimitTimestamp");

      if(!this.operationTypeFile.structuredOperationAssetId) {
        this.operationTypeFile.structuredOperationAssetId = +this.route.snapshot.queryParamMap.get("structuredOperationAssetId");
      }

      if(!this.operationTypeFile.structuredOperationAssetSubtypeId) {
        this.operationTypeFile.structuredOperationAssetSubtypeId = +this.route.snapshot.queryParamMap.get("structuredOperationAssetSubtypeId");
      }
    }

    this.model.id = id;
    this.model.command = "0";
    this.fundoComponent.setEnable(false);

    if (this.mainTicketId && !id) {
      this.service.StructuredOperations.getTicket(this.mainTicketId).subscribe(
        (res) => {
          let data = {
            portfolioDocument: res.portfolioDocument,
            portfolioName: res.portfolioName,
            portfolioFundId: res.portfolioFundId,
            portfolioCetipAccount: res.portfolioCetipAccount,
            portfolioBank: res.portfolioBank,
            portfolioBranch: res.portfolioBranch,
            portfolioAccount: res.portfolioAccount,
            portfolioFundAccountId: res.portfolioFundAccountId,
          };

          this.fundoComponent.fillComponentFieldsLoad(data);
        }
      );
    }

    if (id) {
      this.service.StructuredOperationOrder.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);
          this.model.portfolioDocument = this.validator.formatDocument(
            this.model.portfolioDocument
          );

          if (this.model.counterpartDocument) {
            if (this.model.counterpartType === CounterpartType.PessoaJuridica) {
              this.model.counterpartDocument = this.validator.formatDocument(
                this.model.counterpartDocument
              );
            } else if (
              this.model.counterpartType === CounterpartType.PessoaFisica
            ) {
              this.model.counterpartDocument =
                this.validator.formatPhysicalDocument(
                  this.model.counterpartDocument
                );
            }
          }

          this.model.operationDate = new Date(
            this.model.operationDate
              .toString()
              .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
          );
          this.model.operationDate.setHours(0, 0, 0, 0);

          if (this.model.acquisitionDate != null) {
            this.model.acquisitionDate = this.datePipe.transform(
              this.model.acquisitionDate,
              "dd/MM/yyyy"
            ) as any;
          }

          if (this.model.expirationDate != null) {
            this.model.expirationDate = this.datePipe.transform(
              this.model.expirationDate,
              "dd/MM/yyyy"
            ) as any;
          }

          if (this.model.settlementDate != null) {
            this.model.settlementDate = this.datePipe.transform(
              this.model.settlementDate,
              "dd/MM/yyyy"
            ) as any;
          }

          if (this.model.issueDate != null) {
            this.model.issueDate = this.datePipe.transform(
              this.model.issueDate,
              "dd/MM/yyyy"
            ) as any;
          }

          if (this.model.subscriptionDate != null) {
            this.model.subscriptionDate = this.datePipe.transform(
              this.model.subscriptionDate,
              "dd/MM/yyyy"
            ) as any;
          }

          if (this.model.indexBase != null) {
            this.model.indexBase = +this.decimalPipe.transform(
              this.model.indexBase,
              "1.0-4",
              "pt-BR"
            );
          }

          if (this.model.indexPercent != null) {
            this.model.indexPercent = this.decimalPipe.transform(
              this.model.indexPercent,
              "1.0-4",
              "en-US"
            ) as any;
          }

          if (this.model.indexTax != null) {
            this.model.indexTax = this.decimalPipe.transform(
              this.model.indexTax,
              "1.0-4",
              "pt-BR"
            ) as any;
          }

          if (this.model.issueFee != null) {
            this.model.issueFee = this.decimalPipe.transform(
              this.model.issueFee,
              "1.0-4",
              "pt-BR"
            ) as any;
          }

          if (this.model.counterpartType) {
            this.model.counterpartType = +this.decimalPipe.transform(
              this.model.counterpartType,
              "1.0-4",
              "pt-BR"
            );
          }

          this.model.indexPercent = this.decimalPipe.transform(
            this.model.indexPercent,
            "1.2-2",
            "pt-BR"
          ) as any;

          this.fundoComponent.fillComponentFieldsLoad(this.model);

          this.counterpartComponent.fillComponentFieldsLoad(this.model);

          this.isOtherAssets();
          this.isRealEstateAsset();

          if (this.isOtherAsset || this.isRealty) {
            this.ListaTipoLiquidacao =
              this.service.ListStructuredOrderNoFinancialSettlementTypes();
          }

          this.isEditing = true;
          this.canValidate = true;

          this.dataChanged.emit();

          this.setEnable(false);
        },
        () =>
          this.router.navigate([MenuConstants.Tickets.StructuredOperationOrder])
      );
    } else {
      this.model.operationTypeId = OperationType.Application;
      this.model.settlementTypeId = SettlementType.CETIP;
      this.model.isSecondaryMarket = false;

      this.model.counterpartType = CounterpartType.PessoaJuridica;

      if (!this.model.statusId) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);

        this.model.operationDate = start;
      }
    }
  }

  submit(): void {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.isSubmit = false;

          this.closePage.emit();
        },
        () => {}
      );
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;
    this.model.structuredOperationId ??= this.mainTicketId;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();
    this.fundoComponent.validatePortfolioFields();
    const fundErrors = this.fundoComponent.getErrors();
    const counterpartErrors = this.counterpartComponent.getErrors();

    this.isSubmit =
      f.invalid ||
      errors.length > 0 ||
      fundErrors.length > 0 ||
      counterpartErrors.length > 0;

    if (f.invalid || errors.length > 0) {
      var operationValueError = errors.find((e) => {
        return e == "operationValue";
      });

      if(operationValueError != null && (this.model.operationValue + this.totalOrdersValue) > this.totalApprovedAssetsValue) {
        return throwError("O valor da soma de todas as operações é limitado até a soma do mesmo valor financeiro dos ativos aprovados/boleta mãe.");
      }

      var settlementDateError = errors.find((e) => {
        return e == "settlementDate";
      });

      if(settlementDateError != null) {
        if(!this.validateDate(this.model.settlementDate)) {
          return throwError("A data de liquidação deve ser preenchida.");
        } else if (!this.validateMaxSettlementDate(this.model.settlementDate)) {
          return throwError("A data de liquidação não pode ser posterior às datas de limite definidas nos ativos aprovados/boleta mãe.");
        }
      }

      var amountError = errors.find((e) => {
        return e == "amount";
      });

      if(amountError != null && !this.amountValidator(this.model.amount)) {
        return throwError("A soma das quantidades de todas as operações é limitada até a soma das quantidades dos ativos aprovados/boleta mãe.");
      }

      if (counterpartErrors.length === 0) {
        this.counterpartComponent.isValidating = true;
      }

      if (fundErrors.length === 0) {
        this.fundoComponent.isValidating = true;
      }

      return throwError("Verifique os campos destacados.");
    }

    if (fundErrors.length > 0) {
      return throwError("Verifique os campos nos dados do Fundo Investidor.");
    }

    if (counterpartErrors.length > 0) {
      return throwError("Verifique os campos nos dados da Contraparte.");
    }

    if (!this.isEditing && !this.mainTicketId) {
      return throwError("Ordem não possui id de boleta principal");
    }

    if (this.model.isSecondaryMarket && this.uploadedFilesCount <= 0) {
      return throwError(
        "Para Mercado Secundário é necessário inserir pelo menos um arquivo"
      );
    }

    let data = Object.assign({}, this.model);

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data.indexPercent = this.validator.getCurrency(data.indexPercent);
    data.issueFee = this.validator.getCurrency(data.issueFee);
    data.indexTax = this.validator.getCurrency(data.indexTax);
    data.counterpartDocument = this.validator.sanitize(
      data.counterpartDocument
    );

    if (data.acquisitionDate != null) {
      data.acquisitionDate = new Date(
        data.acquisitionDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.subscriptionDate != null) {
      data.subscriptionDate = new Date(
        data.subscriptionDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.expirationDate != null) {
      data.expirationDate = new Date(
        data.expirationDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }
    if (data.settlementDate != null) {
      data.settlementDate = new Date(
        data.settlementDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }
    if (data.issueDate) {
      data.issueDate = new Date(
        data.issueDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.operationDate) {
      data.operationDate = new Date(
        data.operationDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (!data.counterpartType) {
      data.counterpartType = CounterpartType.PessoaJuridica;
    }

    if (this.isEscritoTermo()) {
      data.assetCode = null;
    } else {
      data.objectAction = null;
    }

    if (!this.isSell()) {
      data.operationCode = null;
    }

    data = this.fundoComponent.fillPortfolioData(
      data,
      this.fundoComponent.portfolio
    );

    data = this.counterpartComponent.fillCounterpartData(
      data,
      this.counterpartComponent.counterpartModel
    );

    data.annotations = this.annotationComponent.annotation;

    if (f.invalid) {
      return throwError("Verifique os campos destacados.");
    }

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.StructuredOperationOrder.updateTicket(
            this.model.id,
            data
          )
        : this.service.StructuredOperationOrder.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((structuredOperationOrder: StructuredOperationOrder) => {
          if (!editing) {
            this.model.id = structuredOperationOrder.id;
            this.model.operationDate = structuredOperationOrder.operationDate;
            this.model.statusId = structuredOperationOrder.statusId;
            this.model.accountManagerName =
              structuredOperationOrder.accountManagerName;
            this.model.accountManagerDocument =
              structuredOperationOrder.accountManagerDocument;
            this.model.ticketCutoffTypeId =
              structuredOperationOrder.ticketCutoffTypeId;

            this.isEditing = true;

            if (this.isComponent) {
              this.reloadTickets.emit();
            } else {
              const url = this.router.createUrlTree([this.model.id], {
                relativeTo: this.route,
              });
              this.location.go(url.toString());
            }
          }

          return this.model;
        })
      );
  }

  getHolidayList() {
    this.calendarService.getHolidaysList().subscribe((res) => {
      res.forEach((x) => {
        x.date = this.datePipe.transform(x.date, "dd/MM/yyyy", "UTC") as any;
      });
      this.holidayList = res;
    });
  }

  amountValidator(value: number) {
    if (value + this.totalOrdersAmount <= this.totalApprovedAssetsAmount) {
      value += this.totalOrdersAmount;
      return true;
    } else {
      return false;
    }
  }

  validateDate(date: Date) {
    if (date != null) {
      if (this.holidayList.length > 0) {
        this.holidayList.forEach((x) => {
          if (date == x.date) {
            return false;
          }
        });
      }
      date = new Date(
        date.toString().replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
      var yesterdayDate = new Date();
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);

      if (date < yesterdayDate || date.getDay() == 0 || date.getDay() == 6) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  validateMaxSettlementDate(date: Date) {
    if(date != null) {
      var dateValues = date.toString().split('/');
      var dateString = dateValues[2] + dateValues[1] + dateValues[0];
      var timestamp = parseInt(dateString);
  
      return timestamp <= this.biggestLimitTimestamp;
    }

    return false;
  }

  operationValueValidator(value: number) {
    if ((value + this.totalOrdersValue) <= this.totalApprovedAssetsValue) {
      value += this.totalOrdersValue;
      return true;
    }
    
    return false;
  }

  calculateOperationValue(): void {
    this.model.operationValue = this.model.amount * this.model.unitPrice;
  }

  transformDecimal(val: number): number {
    return this.decimalPipe.transform(val, "1.0-8", "pt-BR") as any;
  }

  confirmForward(): Promise<boolean> {
    this.closePage.emit();

    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = 'A boleta não poderá mais ser editada, deseja continuar?';

    if(this.isNotProduction) {
      textConfirm += '<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.';
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        textConfirm
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          this.openPage.emit();
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }

      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            this.canValidate = true;
            
            this.openPage.emit();

            return throwError(err);
          })
        )
        .subscribe(
          (structuredOperationOrder: StructuredOperationOrder) => {
            this.service.StructuredOperationOrder.forwardTicket(
              structuredOperationOrder.id
            ).subscribe((res) => {
              this.model.statusDescription = res.statusDescription;
              this.canValidate = true;

              this.setEnable(false);

              this.snackBarService.open("Boleta encaminhada.", "OK", {
                verticalPosition: "top",
                horizontalPosition: "right",
              });

              location.reload();
            });
          },
          () => {}
        );
    });
  }

  cancel() {
    this.closePage.emit();

    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.StructuredOperationOrder.cancelDraftTicket(
              this.model.id
            );
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          if (this.isComponent) {
            this.reloadTickets.emit();
          } else {
            this.getTicket(this.model.id);
          }
        }
      });
  }

  approve(): void {
    this.closePage.emit();

    this.dialogService
      .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service.approve(this.model.id, "").subscribe(() => {
            this.snackBarService.open("Boleta atualizada com sucesso.", "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });

            if (this.isComponent) {
              this.reloadTickets.emit();
            } else {
              this.getTicket(this.model.id);
            }
          });
        }
      });
  }

  reject(): void {
    this.closePage.emit();

    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result?.justification) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open(
                "Boleta atualizada com sucesso.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );

              if (this.isComponent) {
                this.reloadTickets.emit();
              } else {
                this.getTicket(this.model.id);
              }
            });
        }
      });
  }
  getBanksList() {
    this.domainService.ListBanks().subscribe((b) => {
      this.banksList = b;
    });
  }
  coalesce(value: any) {
    return value || "";
  }

  isSell(): boolean {
    return this.model.operationTypeId === OperationType.Redemption;
  }

  isNoFinancial(): boolean {
    return this.model.settlementTypeId === SettlementType.NoFinancial;
  }

  isTED(): boolean {
    return this.model.settlementTypeId === SettlementType.TED;
  }

  isCETIP(): boolean {
    return this.model.settlementTypeId === SettlementType.CETIP;
  }

  isPurchase(): boolean {
    return this.model.operationTypeId === OperationType.Application;
  }

  isDeposit(): boolean {
    return this.model.operationTypeId === OperationType.Deposit;
  }

  isCetipVoice(): boolean {
    if (this.isCETIP() && this.isPurchase()) {
      switch (this.model.assetType) {
        case "DEB":
        case "CRA":
        case "CRI":
          return true;
        default:
          return false;
      }
    }
  }

  isNaoPadronizados(): boolean {
    if ((this.isCETIP() || this.isTED()) && this.isPurchase()) {
      switch (this.model.assetType) {
        case "DEB":
        case "CRA":
        case "CRI":
        case "CCI":
        case "CCE":
        case "CCB":
        case "CPR":
          return true;
        default:
          return false;
      }
    }
  }

  isWithdrawal(): boolean {
    return this.model.operationTypeId === OperationType.Withdrawal;
  }

  isFisica(): boolean {
    return this.model.counterpartType === +CounterpartType.PessoaFisica;
  }

  isJuridica(): boolean {
    return this.model.counterpartType === +CounterpartType.PessoaJuridica;
  }

  /**
   * @description Is asset type equals 'termo'
   */
  isEscritoTermo(): boolean {
    return (
      this.model.assetType !== null &&
      this.model.assetType.toString().toLowerCase() === "termo"
    );
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    var hasError = !this.validators[prop](this.model[prop]);

    return hasError;
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });
    return validationErrors;
  }

  duplicate(): void {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.CETIP },
          ]);
        });
    });
  }

  openTicketOnNewTab() {
    var structuredOperationAssetId = this.structuredOperationAssetId;
    var structuredOperationAssetSubtypeId = this.structuredOperationAssetSubtypeId;
    var mainTicketId = this.mainTicketId ?? this.model.structuredOperationId;
    var totalApprovedAssetsValue = this.totalApprovedAssetsValue;
    var totalApprovedAssetsAmount = this.totalApprovedAssetsAmount;
    var totalOrdersValue = this.totalOrdersValue;
    var totalOrdersAmount = this.totalOrdersAmount;
    var biggestLimitTimestamp = this.biggestLimitTimestamp;

    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        [`${MenuConstants.Tickets.StructuredOperationOrder}/${this.model.id}`],
        {
          queryParams: {
            mainTicketId,
            totalApprovedAssetsValue,
            totalApprovedAssetsAmount,
            totalOrdersValue,
            totalOrdersAmount,
            biggestLimitTimestamp,
            structuredOperationAssetId,
            structuredOperationAssetSubtypeId
          },
        }
      )
    );

    this.closePage.emit();
    window.open(url, "_blank");
  }

  showNewTabButton() {
    return this.isComponent;
  }

  setUploadedFilesCount(filesCount: number) {
    this.uploadedFilesCount = filesCount;
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateStructuredOperationOrderTicket)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.StructuredOperationOrder;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundoComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateStructuredOperationOrderTicket) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateStructuredOperationOrderTicket)
    );
  }

  isOtherAssets() {
    this.isOtherAsset =
      this.operationTypeFile.structuredOperationAssetSubtypeId ==
        StructuredOperationAssetsSubtypesEnum.JudicialActions ||
      this.operationTypeFile.structuredOperationAssetSubtypeId ==
        StructuredOperationAssetsSubtypesEnum.Precatory;
  }

  isRealEstateAsset() {
    return (this.isRealty =
      this.operationTypeFile.structuredOperationAssetSubtypeId ==
      StructuredOperationAssetsSubtypesEnum.Realty);
  }

  isFieldAvailable(field: String) {
    let arrayReference = [];
    switch(this.operationTypeFile.structuredOperationAssetSubtypeId) {
      case 1: //"CCB":
      case 2: //"CRA":
      case 3: //"CRI":
      case 4: //"Debênture":
        arrayReference = this.clusterAFieldNames;
        break;
      case 5: //"Imóvel":
        arrayReference = this.clusterEFieldNames;
        break;
      case 7: //"Ações Judiciais":
        arrayReference = this.clusterBFieldNames;
        break;
      case 8: //"Precatório":
        arrayReference = this.clusterCFieldNames;
        break;
      case 9: //"Companhia Aberta":
      case 10: //"Companhia Fechada":
      case 11: //"Empresa Limitada":
      case 12: //"Sociedade de Propósito Específico (SPE)":
        arrayReference = this.clusterDFieldNames;
        break;
    }

    return arrayReference.indexOf(field) >= 0;
  }
}
